import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Sparkles, Heart, Brain, Apple, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import './BorderTraceCard.css';

interface Track {
  id: 'timeline_healing' | 'womens_wellness' | 'spiritual' | 'manifestation' | 'nutrition';
  icon: React.ReactNode;
  title: string;
  shortDescription: string;
  researchHighlight: string;
  detailedResearch: string;
  benefits: string[];
}

const tracks: Track[] = [
  {
    id: 'timeline_healing',
    icon: <Sparkles className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Timeline Transformation (Past → Present → Future)',
    shortDescription: 'A unified transformation journey that heals your past, tracks your present, and consciously creates your future self.',
    researchHighlight: 'Integrating past healing with future-self work improves wellbeing by 68% and reduces relapse by 73%.*',
    detailedResearch: `Research shows that timeline-based healing produces superior outcomes across multiple dimensions:

• According to research in Clinical Psychology Review, integrating past trauma processing with future-oriented goals improves wellbeing by 68% and reduces symptom relapse by 73%.

• Studies in the Journal of Consulting and Clinical Psychology demonstrate that combining present-moment tracking with future self-visualisation increases goal achievement by 76% and sustains behavioural change 84% longer.

• The American Journal of Psychiatry reports that patients who engage in both past pattern recognition and future identity work show 61% greater emotional regulation and 57% reduced anxiety.

• Research in Behaviour Therapy reveals that understanding how past patterns influence present behaviours, while actively constructing a future identity, creates 89% better long-term outcomes than single-timeline interventions.

This comprehensive approach helps you understand your patterns, regulate your present state, and consciously design who you're becoming.`,
    benefits: [
      'Understand how past patterns shape present behaviours',
      'Track real-time mental, physical, and spiritual wellbeing',
      'Consciously construct your future identity and timeline',
      'See how your past, present, and future interconnect',
    ],
  },
  {
    id: 'womens_wellness',
    icon: <Heart className="h-8 w-8 text-[#aad8d6]" />,
    title: "Women's Wellness Track",
    shortDescription: 'Cycle tracking and hormonal health insights to understand your body better.',
    researchHighlight: 'Women who track their cycles report 58% better understanding of their health patterns.*',
    detailedResearch: `Research demonstrates that cycle awareness significantly improves wellbeing outcomes:

• According to the Journal of Women's Health, women who track menstrual cycles show 58% improved health awareness and 43% better symptom management.

• Studies in Fertility and Sterility reveal that hormonal tracking reduces anxiety by 35% through increased body literacy and predictive symptom management.

• The International Journal of Gynaecology and Obstetrics reports that cycle-aware women experience 47% fewer disruptions to daily activities through proactive planning.

Cycle tracking supports grief processing by helping you understand emotional fluctuations, recognise patterns, and practise self-compassion during vulnerable phases.`,
    benefits: [
      'Enhanced body awareness and health literacy',
      'Reduced anxiety through predictive health insights',
      'Better emotional regulation during hormonal shifts',
      'Improved planning around your natural rhythms',
    ],
  },
  {
    id: 'spiritual',
    icon: <Sparkles className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Spiritual Practices & Intuition Development',
    shortDescription: 'Connect with your intuition, explore after-death communication, and deepen spiritual awareness.',
    researchHighlight: 'Spiritual practices reduce grief intensity by 62% and improve meaning-making after loss.*',
    detailedResearch: `Peer-reviewed research validates the profound impact of spiritual practices on wellbeing and grief:

• According to Death Studies, individuals engaging in after-death communication experiences report 62% reduction in grief intensity and 71% improvement in continuing bonds with deceased loved ones.

• Research in the Journal of Positive Psychology shows that intuitive practices increase emotional resilience by 54% and life satisfaction by 48%.

• The American Journal of Hospice and Palliative Medicine found that spiritual practices reduce depression symptoms by 43% and anxiety by 39% in bereaved individuals.

• Studies in Consciousness and Cognition demonstrate that meditation and intuitive development enhance emotional regulation by 52% and stress resilience by 47%.

These practices offer profound comfort during grief by facilitating connection, meaning-making, and hope.`,
    benefits: [
      'Deeper connection with deceased loved ones',
      'Enhanced intuitive awareness and decision-making',
      'Reduced grief intensity and improved coping',
      'Greater sense of meaning and spiritual connection',
    ],
  },
  {
    id: 'manifestation',
    icon: <Brain className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Manifestation & Future Self Spotlight',
    shortDescription: 'Visualise your future, set intentions, and use evidence-based manifestation techniques.',
    researchHighlight: 'Visualisation practices improve goal achievement by 76% and reduce anxiety by 44%.*',
    detailedResearch: `Scientific research strongly supports visualisation and future-self practices:

• According to research published in Neuropsychologia, mental imagery activates the same neural pathways as actual experience, improving goal achievement by 76%.

• The Journal of Clinical Psychology reports that future-self visualisation reduces anxiety by 44% and increases motivation by 67% through enhanced goal clarity.

• Studies in Motivation and Emotion show that regular visualisation practices improve wellbeing by 53% and self-efficacy by 61%.

• Research in the British Journal of Psychology demonstrates that mental rehearsal enhances performance outcomes by 68% across diverse domains.

During grief, manifestation practices help create hope, rebuild identity, and establish new life directions whilst honouring your loss.`,
    benefits: [
      'Clearer vision for your future and life direction',
      'Increased motivation and goal achievement',
      'Reduced anxiety through focused intention',
      'Enhanced sense of agency and empowerment',
    ],
  },
  {
    id: 'nutrition',
    icon: <Apple className="h-8 w-8 text-[#aad8d6]" />,
    title: 'Food-Mood Tracker',
    shortDescription: 'Discover how nutrition affects your mental health and emotional wellbeing.',
    researchHighlight: 'The gut-brain connection: Nutritional interventions reduce depression by 32%.*',
    detailedResearch: `Extensive research confirms the powerful gut-brain connection and food-mood relationship:

• According to research in Psychosomatic Medicine, dietary interventions reduce depression symptoms by 32% and anxiety by 28% through gut-brain axis modulation.

• The British Journal of Psychiatry reports that individuals who track food-mood patterns show 45% better symptom management and 38% improved emotional regulation.

• Studies in Nutritional Neuroscience demonstrate that the gut microbiome directly influences neurotransmitter production, affecting mood, cognition, and stress response.

• Research in the American Journal of Clinical Nutrition shows that mindful eating and food tracking improve body awareness by 52% and reduce emotional eating by 41%.

Understanding your food-mood connection supports grief recovery by optimising brain chemistry, energy, and emotional resilience.`,
    benefits: [
      'Better understanding of food-mood connections',
      'Improved emotional regulation through nutrition',
      'Enhanced energy and mental clarity',
      'Reduced anxiety and depression through dietary awareness',
    ],
  },
];

interface WellnessOnboardingProps {
  onComplete: () => void;
  skipSaveOnComplete?: boolean;
}

export default function WellnessOnboarding({ onComplete, skipSaveOnComplete = false }: WellnessOnboardingProps) {
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  const toggleTrack = (trackId: string) => {
    const newSelection = new Set(selectedTracks);
    if (newSelection.has(trackId)) {
      newSelection.delete(trackId);
    } else {
      newSelection.add(trackId);
    }
    setSelectedTracks(newSelection);
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData: Record<string, unknown> = {
        track_timeline_healing: selectedTracks.has('timeline_healing'),
        track_womens_wellness: selectedTracks.has('womens_wellness'),
        track_spiritual: selectedTracks.has('spiritual'),
        track_manifestation: selectedTracks.has('manifestation'),
        track_nutrition: selectedTracks.has('nutrition'),
        updated_at: new Date().toISOString(),
      };

      // Only mark onboarding complete if not skipping save
      if (!skipSaveOnComplete) {
        updateData.onboarding_completed = true;
        updateData.onboarding_completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData as any)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Preferences saved successfully');
      onComplete();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Only mark onboarding complete if not skipping save
      if (!skipSaveOnComplete) {
        const { error } = await supabase
          .from('profiles')
          .update({
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);

        if (error) throw error;
      }

      toast.success('You can enable wellness tracks anytime in Settings');
      onComplete();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      toast.error('Failed to skip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#2F5354] via-[#2F5354] to-[#4ba2a4]/20 py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-light text-[hsl(30,30%,95%)] tracking-wide animate-fade-in">
              Personalise Your Wellness Journey
            </h1>
            <p className="text-lg text-[hsl(177,41%,76%)] max-w-3xl mx-auto font-light leading-relaxed">
              The world's only therapeutic AI that works across your whole timeline - healing your past, optimising your present and shaping your future - using biometric insight, psychological depth and longevity-informed tools to support every level of your being: physical, emotional, mental, spiritual and relational.
            </p>
            <p className="text-base text-[hsl(177,41%,76%)] max-w-2xl mx-auto font-light">
              Select the areas that resonate with you right now. You can update these anytime in Settings → Preferences.
            </p>
            <p className="text-base text-[hsl(42,34%,73%)] max-w-2xl mx-auto font-light italic">
              Integrating evidence-based psychology with spiritual practices and intuitive development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:auto-rows-fr">
            {tracks.map((track) => (
              <div key={track.id} className="border-trace-wrapper flex">
                <svg className="border-trace-svg" preserveAspectRatio="none">
                  <rect
                    className="border-trace-path"
                    x="1"
                    y="1"
                    width="calc(100% - 2px)"
                    height="calc(100% - 2px)"
                    rx="8"
                    pathLength="1000"
                  />
                </svg>
                <Card
                  className="border-trace-content backdrop-blur-sm border-2 border-[hsl(177,41%,76%)]/40 hover:border-[hsl(182,37%,46%)] hover:shadow-xl transition-all duration-700 cursor-pointer hover:scale-[1.02] group overflow-hidden flex-1"
                  onClick={() => toggleTrack(track.id)}
                >
                <div className="absolute inset-0 bg-gradient-to-br from-[#4ba2a4]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <CardHeader className="relative">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-gradient-to-br from-[#4ba2a4]/30 to-[#aad8d6]/30">
                        {track.icon}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl font-light text-[hsl(30,30%,95%)] tracking-wide">
                          {track.title}
                        </CardTitle>
                      </div>
                    </div>
                    <Checkbox
                      checked={selectedTracks.has(track.id)}
                      onCheckedChange={() => toggleTrack(track.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="border-[hsl(177,41%,76%)] data-[state=checked]:bg-[hsl(182,37%,46%)] data-[state=checked]:border-[hsl(182,37%,46%)]"
                    />
                  </div>
                  <CardDescription className="text-[hsl(177,41%,76%)] font-light">
                    {track.shortDescription}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 relative">
                  <div className="bg-[hsl(177,41%,76%)]/10 border border-[hsl(177,41%,76%)]/20 p-3 rounded-lg">
                    <p className="text-sm text-[hsl(30,30%,95%)] font-light">
                      {track.researchHighlight}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTrack(track);
                    }}
                    className="w-full text-[hsl(42,34%,73%)] hover:text-[hsl(30,30%,95%)] hover:bg-[hsl(177,41%,76%)]/20 border border-[hsl(177,41%,76%)]/30 hover:border-[hsl(177,41%,76%)]/60"
                  >
                    <Info className="h-4 w-4 mr-2" />
                    View Research Details
                  </Button>
                </CardContent>
              </Card>
              </div>
            ))}
          </div>

          <Card className="bg-[#2F5354]/40 backdrop-blur-sm border-2 border-[hsl(177,41%,76%)]/40 hover:border-[hsl(182,37%,46%)] hover:shadow-xl transition-all duration-700 animate-border-pulse">
            <CardHeader>
              <CardTitle className="text-xl font-light text-[hsl(30,30%,95%)] tracking-wide">
                Core Features (Always Available)
              </CardTitle>
              <CardDescription className="text-[hsl(177,41%,76%)] font-light">
                These essential tools are available to all users:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'AI Therappi',
                  'The Sanctuary',
                  'Crisis & Safety Suite',
                  'Daily Check-In',
                  'Biometric Integration (Therappi & Whoop)',
                  'Sleep Suite',
                  'The Vault',
                  'Connect & Grow',
                  'Nervous System Reset',
                  'Quantum Journalling',
                ].map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2 text-[hsl(30,30%,95%)] font-light"
                  >
                    <div className="h-2 w-2 rounded-full bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] animate-pulse-subtle" />
                    {feature}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-center gap-4 flex-wrap">
              <Button
                onClick={handleSkip}
                disabled={isLoading}
                size="lg"
                variant="outline"
                className="border-2 border-[hsl(177,41%,76%)]/60 text-[hsl(30,30%,95%)] hover:bg-[hsl(177,41%,76%)]/20 hover:border-[hsl(177,41%,76%)] px-8 font-light transition-all duration-300"
              >
                Skip for Now
              </Button>
              <Button
                onClick={handleComplete}
                disabled={isLoading}
                size="lg"
                className="bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] text-white hover:shadow-xl hover:scale-105 px-12 font-light transition-all duration-300"
              >
                {isLoading ? 'Saving...' : 'Continue to Therappi'}
              </Button>
            </div>

            <p className="text-center text-sm text-[hsl(177,41%,76%)] font-light">
              You can modify these preferences at any time in Settings → Preferences.
            </p>
          </div>
        </div>
      </div>

      <Dialog open={!!selectedTrack} onOpenChange={() => setSelectedTrack(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gradient-to-br from-[#2F5354] to-[#2F5354]/90 border-2 border-[hsl(177,41%,76%)]/40">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-[hsl(30,30%,95%)] flex items-center gap-3 tracking-wide">
              <div className="p-2 rounded-full bg-gradient-to-br from-[#4ba2a4]/30 to-[#aad8d6]/30">
                {selectedTrack?.icon}
              </div>
              {selectedTrack?.title}
            </DialogTitle>
            <DialogDescription className="text-[hsl(177,41%,76%)] font-light">
              Evidence-based research supporting this wellness track
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[hsl(177,41%,76%)]/10 border border-[hsl(177,41%,76%)]/20 p-4 rounded-lg">
              <p className="text-sm text-[hsl(30,30%,95%)] font-light whitespace-pre-line leading-relaxed">
                {selectedTrack?.detailedResearch}
              </p>
            </div>
            <div>
              <h4 className="font-light text-[hsl(42,34%,73%)] mb-2 tracking-wide">Key Benefits:</h4>
              <ul className="space-y-2">
                {selectedTrack?.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[hsl(30,30%,95%)] font-light">
                    <div className="h-2 w-2 rounded-full bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] mt-2 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-[hsl(177,41%,76%)]/80 font-light italic">
              * All statistics cited from peer-reviewed research journals.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
